import type { ChangeEvent, KeyboardEvent, DragEvent } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'
import { useState, useEffect, useRef, useCallback } from 'react'
import { MdSend, MdAttachment } from 'react-icons/md'
import { useDispatch } from 'react-redux'

import http from '../../../lib/http'
import { getChatMessagesQueryKey, getChatsQueryKey } from '../../chats'
import { ErrorPlaceHolder, NoDataPlaceHolder } from '../../PatientFolder/behavioral/components'
import { ErrorToast } from '../../shared/error-toast'
import { CircularProgress } from '../../shared/NarusMaterial'
import { ensureChatSelected } from '../actions'
import { humanName } from '../utils'

import ChatBody from './chat-body'
import { MessageAttachmentModal } from './message-attachment-modal'
import { TextInput } from './text-input'

interface getIsScrolledToBottomProps {
  scrollHeight: number
  scrollTop: number
  clientHeight: number
}

function getIsScrolledToBottom ({ scrollHeight, scrollTop, clientHeight }: getIsScrolledToBottomProps): boolean {
  const calculated = scrollTop + clientHeight
  const margins = [ scrollHeight - 30, scrollHeight + 30 ]

  if ((margins[0] != null) && (margins[1] != null)) {
    return calculated >= margins[0] && calculated <= margins[1]
  }
  return false
}

function isValidStatus (status: number): boolean {
  return status >= 200 && status < 300
}

export const SUPPORTED_FILETYPE = [ 'image/png', 'image/jpeg', 'image/gif', 'application/pdf' ]
const SUPPORTED_FILETYPE_DISPLAY = 'png, jpeg, pdf, gif'

interface Participant {
  id: string
  resourceType: string
  name: string
  active: boolean
}

interface Chat {
  id: string
  clientName: string
  meta: {
    concierge?: {
      id: string
    }
  }
  participants: Participant[]
  messages?: object
}

interface ActiveChatProps {
  currentUserId: string
  loadMoreHistory: () => void
  clientName: string
  messagesLoading: boolean
  tagsSetting: []
  chat?: Chat
  status?: number
  sendMessage: (message: string, uploadId: string | null, id: string, fileName: string | undefined, callback: () => Promise<void>) => void
  locationKey: string
  getUrlPreview: () => void
  sendMessageStart: (message: string, id: string) => void
  sendMessageError: (id: string) => void
}

export default function ActiveChat ({
  currentUserId,
  loadMoreHistory,
  clientName,
  messagesLoading,
  tagsSetting,
  chat,
  status,
  sendMessage,
  locationKey,
  getUrlPreview,
  sendMessageStart,
  sendMessageError
}: ActiveChatProps): JSX.Element {
  const [ isSending, setSending ] = useState<boolean>(false)
  const [ message, setMessage ] = useState<string>('')
  const [ drag, setDrag ] = useState<boolean>(false)
  const [ preview, setPreview ] = useState<string | null>(null)
  const [ shiftDown, setShiftDown ] = useState<boolean>(false)
  const [ attachmentError, setAttachmentError ] = useState<{ message: string } | null>(null)
  const bottomAnchorRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!isSending) {
      textAreaRef.current?.focus()
    }
  }, [ chat, isSending ])

  let chatId = chat?.id
  let dispatch = useDispatch()

  useEffect(() => {
    if (!chatId) {
      return
    }

    // @ts-expect-error // I know what I'm doing.
    dispatch(ensureChatSelected(chatId))
  }, [ chatId, dispatch ])

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    setMessage(event?.target?.value)

    const { current: body } = bodyRef
    if (!body) {
      return
    }

    const isScrolledToBottom = getIsScrolledToBottom(body)

    if (!isScrolledToBottom) {
      return
    }

    body.scrollBy({
      top: body.scrollHeight,
      left: 0
    })
  }

  const setError = (m: string): void => {
    setAttachmentError({ message: m })
    setTimeout(() => setAttachmentError(null), 10000)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.keyCode === 16 || e.charCode === 16) {
      setShiftDown(true)
    }
  }

  const handleKeyUp = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.keyCode === 16 || e.charCode === 16) {
      setShiftDown(false)
    }
  }

  const getFile = (): File | null => {
    const file: File = _.get(fileRef?.current, 'files[0]')
    if (_.isNil(file)) {
      return null
    }

    return file
  }

  function isFileSelected (): boolean {
    return !!getFile()
  }

  interface FileResponse {
    files: {
      file0: {
        id: string
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const doUpload = (newMessageId: string, message: string) => {
    if (!isFileSelected()) {
      return null
    }

    const file: File = _.get(fileRef.current, 'files[0]')
    const formData = new FormData()

    formData.append('file0', file, file.name)

    if (fileRef.current) {
      fileRef.current.value = ''
    }

    return http
      .postRequest<FileResponse>('/documents', formData, { clientName })
      .then((r) => r)
      .catch((e: any) => {
        setError(e?.response?.data?.error)
        if (message.length === 0) {
          sendMessageError(newMessageId)
        }
      })
  }

  async function invalidateNewChat (): Promise<void> {
    let messagesQueryKey = getChatMessagesQueryKey({ userId: currentUserId, chatId })
    let chatsQueryKey = getChatsQueryKey({ userId: currentUserId })
    await queryClient.invalidateQueries({ queryKey: messagesQueryKey, exact: false })
    await queryClient.invalidateQueries({ queryKey: chatsQueryKey, exact: false })
  }

  const onSendMessage = async (message: string): Promise<void> => {
    const processedMessage: string = message?.trim() || ''
    const newMessageId: string = Math.random().toString(16).substring(2)
    sendMessageStart(processedMessage, newMessageId)
    setPreview(null)
    setMessage('')
    const file = getFile()
    let uploadId = null

    if (file && SUPPORTED_FILETYPE.includes(file?.type)) {
      uploadId = (await doUpload(newMessageId, processedMessage))?.files?.file0?.id ?? null
    }

    if ((uploadId !== null) || processedMessage.length > 0) {
      sendMessage(processedMessage, uploadId, newMessageId, file?.name, invalidateNewChat)
    }

    if (((bottomAnchorRef?.current?.offsetTop) != null) && ((bodyRef?.current?.scrollTop) != null)) {
      const scrollTo = (bottomAnchorRef?.current?.offsetTop || 0) - (bodyRef?.current?.scrollTop || 0)

      if (scrollTo) {
        bodyRef.current?.scrollBy({
          top: scrollTo,
          left: 0
        })
      }
    }
  }

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if ((e.key !== 'Enter' && e.key !== 'Enter') || shiftDown) {
      return
    }

    e.preventDefault()

    if (getFile() === null && _.isEmpty(message.trim())) {
      return
    }

    void onSendMessage(message)
  }

  const handleDrag = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleInputChange = (): undefined => {
    const file: File | undefined = _.get(fileRef.current, 'files[0]')

    if (!file) {
      return
    }

    if (!SUPPORTED_FILETYPE.includes(file?.type)) {
      setError(`${file?.type || 'Undefined file type'} is not supported.\nSupported attachment types: ${SUPPORTED_FILETYPE_DISPLAY}.`)
      return
    }

    if (file?.size > 10485760) {
      setError('Attachment larger than 10MB.')
      return
    }

    const { type } = file

    if (type === 'application/pdf') {
      setPreview('pdf')
      return
    }

    try {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onloadend = () => {
        setPreview(reader.result?.toString() ?? null)
      }
    } catch (e) {
      console.warn(e)
    }
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()

    setDrag(false)

    if (e.dataTransfer?.files?.length > 0) {
      if (fileRef.current) {
        fileRef.current.files = e.dataTransfer.files
      }
      handleInputChange()
    }
  }

  const onClearAttachment = (): void => {
    if ((fileRef.current?.value) != null) {
      fileRef.current.value = ''
    }
    setPreview(null)
  }

  const handleDragOut = useCallback((e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    // @ts-expect-error // it's okay
    if (e.currentTarget.contains(e.relatedTarget)) return
    setDrag(false)
  }, [])

  const handleDragIn = useCallback((e: DragEvent<HTMLDivElement>): void => {
    if (drag) return
    e.preventDefault()
    e.stopPropagation()
    setDrag(true)
  }, [ drag ])

  const attachmentButton = (
    <button
      key={1}
      title='Attach Document/Photo'
      type='button'
      data-cy='attach-btn'
      disabled={messagesLoading}
      onClick={() => fileRef.current?.click()}
    >
      <MdAttachment />
    </button>
  )

  const sendButton = (
    <button
      key={2}
      type='button'
      data-cy='send-btn'
      title='Send Message'
      className='colored'
      disabled={!message?.trim()?.length || messagesLoading}
      onClick={() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        onSendMessage(message)
      }}
    >
      <MdSend />
    </button>
  )

  const actions = {
    left: [ attachmentButton ],
    right: [ sendButton ]
  }

  const patientTabColor = {
    color: '#E5A500',
    background: '#FFEAB3'
  }

  const myTabColor = {
    color: '#7F46F9',
    background: '#DFD3FB'
  }

  const otherTabColor = {
    color: '#6F6F6F',
    background: '#F1F1F1'
  }

  if (typeof status === 'number' && !isValidStatus(status)) {
    return (
      <div className='active-chat'>
        <div className='no-data-chat'>
          {status === 404 && (
            <NoDataPlaceHolder
              title='Chat not found'
            />
          )}

          {status === 403 && (
            <ErrorPlaceHolder
              title='You are not authorized to view this chat'
            />
          )}

          {status !== 403 && status !== 404 && (
            <ErrorPlaceHolder
              title='Something went wrong'
            />
          )}
        </div>
      </div>
    )
  }

  if (!chat) {
    return (
      <div
        onDrop={(e) => handleDrop(e)}
        onDragOver={(e) => handleDrag(e)}
        onDragEnter={(e) => handleDragIn(e)}
        onDragLeave={(e) => handleDragOut(e)}
        className='active-chat'
        data-cy='draggable-file-area'
      >
        <div className='no-data-chat'>
          <NoDataPlaceHolder
            title='Select a chat to start messaging'
          />
        </div>
      </div>
    )
  }

  const isConciergeUser = (p: Participant): boolean => {
    return (chat.meta.concierge && chat.participants.some(pr => pr.id === p.id && p.resourceType === 'Practitioner')) ?? false
  }

  function NoMessages (): JSX.Element {
    if (messagesLoading) {
      return <CircularProgress color='purple' />
    }
    return (
      <NoDataPlaceHolder
        title='No Messages'
      />
    )
  }

  const textareaValue = (preview === null) ? message : ''

  return (
    <div
      onDrop={(e) => handleDrop(e)}
      onDragOver={(e) => handleDrag(e)}
      onDragLeave={(e) => handleDragOut(e)}
      onDragEnter={(e) => handleDragIn(e)}
      className='active-chat'
      data-cy='draggable-file-area'
    >
      <input
        data-cy='file-input'
        type='file'
        ref={fileRef}
        accept='image/jpeg, image/png, application/pdf, image/gif'
        onChange={handleInputChange}
        style={{
          display: 'none',
          position: 'absolute',
          width: '100%',
          height: '100%'
        }}
      />
      <div className='chat-sub-title'>
        <h2>Messages</h2>
        <div
          style={{
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap'
          }}
        >
          {chat?.participants
            ?.map(p => {
              const name = humanName(p.name)
              if (p.resourceType === 'Patient') {
                return (
                  <span
                    key={name}
                    style={patientTabColor}
                  >
                    {(p.active && name) || `${name} (inactive)`}
                  </span>
                )
              }
              if (p.id === currentUserId || isConciergeUser(p)) {
                return (
                  <span
                    key={name}
                    style={myTabColor}
                  >
                    {(p.active && name) || `${name} (inactive)`}
                  </span>
                )
              }
              return (
                <span
                  key={name}
                  style={otherTabColor}
                >
                  {(p.active && name) || `${name} (inactive)`}
                </span>
              )
            })}
        </div>
      </div>
      {drag && (
        <div
          className='drop-target'
          data-cy='drop-target'
        >
          <div className='text'>
            <div>Drop file here</div>
          </div>
        </div>
      )}
      {Object.keys(chat.messages ?? {})?.length > 0
        ? (
          <ChatBody
            locationKey={locationKey}
            chat={chat}
            currentUserId={currentUserId}
            onMoreHistory={loadMoreHistory}
            clientName={clientName}
            messagesLoading={messagesLoading}
            tagsSetting={tagsSetting}
            bottomAnchorRef={bottomAnchorRef}
            bodyRef={bodyRef}
            getUrlPreview={getUrlPreview}
          />
        )
        : <div className='no-data-chat'><NoMessages /></div>}
      <div className='chat-input'>
        <TextInput
          value={textareaValue}
          placeholder='Write a message...'
          actions={actions}
          textInputRef={textAreaRef}
          setSending={setSending}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onKeyPress={handleKeyPress}
          onKeyUp={handleKeyUp}
        />
      </div>
      {Boolean(preview) && (
        <MessageAttachmentModal
          value={message}
          placeholder='Write a message...'
          preview={preview}
          meta={getFile()}
          setSending={setSending}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onKeyPress={handleKeyPress}
          onKeyUp={handleKeyUp}
          onCancel={onClearAttachment}
          onSendMessage={onSendMessage}
        />
      )}
      <ErrorToast message={attachmentError?.message} />
    </div>
  )
}
